import React from 'react';
import { observer } from 'mobx-react';
import { StopCardRowIcon, type StopCardRowIconStatus } from '@routific/hive';
import { OrderCompletionStatus, VisitDtoModelType } from '../../../store/mst/models';
import { IColor } from '../../../types/IColor';
import { TimelineViolationTypes } from '../../../helpers/lateness';
import OrderWarning, { OrderWarningType } from '../../orders/OrderWarning';

const STOP_CARD_ROW_ICON_STATUS = {
  reattempt: 'reattempt',
  complete: 'complete',
  missed: 'missed',
} as const satisfies Record<'reattempt' | 'complete' | 'missed', StopCardRowIconStatus>;

interface Props {
  visit: VisitDtoModelType;
  stopUuid?: string;
  color?: IColor;
}

const VisitIcon: React.FC<Props> = ({ visit, stopUuid, color }) => {
  if (!visit?.hasLoaded('attempts')) return null;
  const attemptForStop = visit.attempt(stopUuid);

  const hasLatenessViolation = !!visit.timeline?.violations?.find(
    (v) => v.type === TimelineViolationTypes.LATENESS || v.type === TimelineViolationTypes.EARLINESS,
  );
  const hasDateMismatch = visit.isDateMismatched;
  const completed =
    attemptForStop?.status === OrderCompletionStatus.MISSED ||
    attemptForStop?.status === OrderCompletionStatus.DELIVERED;

  const hasTagMismatchViolation = !!visit.timeline?.violations?.find((v) => v.type === TimelineViolationTypes.TAG_MISMATCH);
  const isQuestionable = !!visit.isQuestionable;
  const warningTypes = [];

  if (hasLatenessViolation && !completed) warningTypes.push(OrderWarningType.LATENESS);
  if (hasDateMismatch) warningTypes.push(OrderWarningType.DATE_MISMATCH);
  if (isQuestionable) warningTypes.push(OrderWarningType.QUESTIONABLE_ADDRESS);
  if (hasTagMismatchViolation && !completed) warningTypes.push(OrderWarningType.TAG_MISMATCH);

  if (warningTypes.length > 0) {
    return <StopCardRowIcon><OrderWarning types={warningTypes} /></StopCardRowIcon>;
  }

  if (attemptForStop?.reattemptRequestDate) {
    return <StopCardRowIcon status={STOP_CARD_ROW_ICON_STATUS.reattempt} />;
  }


  if (attemptForStop?.status === OrderCompletionStatus.DELIVERED) {
    return <StopCardRowIcon status={STOP_CARD_ROW_ICON_STATUS.complete} color={color} />;
  }

  if (attemptForStop?.status === OrderCompletionStatus.MISSED) {
    return <StopCardRowIcon status={STOP_CARD_ROW_ICON_STATUS.missed} />;
  }

  return <StopCardRowIcon />;
};

export default observer(VisitIcon);
